/**
 * HomeSidebar — slide-in menu on the native Home screen (owner brief:
 * the old More tab moved here so its tab-bar slot could go to Profile).
 *
 * A right-edge drawer over a dim backdrop: tool links (the More list),
 * the mandatory "Reset app data" control, and the About line. Enter and
 * exit both animate; the panel stays mounted through the exit transition.
 * Backdrop tap, the X, and navigation all close it.
 */
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Icon } from '@/components/icons';
import { MORE_LINKS } from '@/pages/More';

export function HomeSidebar({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  // Mounted while open OR while the close transition plays out.
  const [mounted, setMounted] = useState(open);
  // Drives the transition classes; flips one frame after mount so the
  // panel actually animates in instead of appearing at its end state.
  const [shown, setShown] = useState(false);
  const raf = useRef(0);

  useEffect(() => {
    if (open) {
      setMounted(true);
      raf.current = requestAnimationFrame(() =>
        requestAnimationFrame(() => setShown(true)),
      );
    } else {
      setShown(false);
    }
    return () => cancelAnimationFrame(raf.current);
  }, [open]);

  // Scroll lock while open (same contract as the drawers) - without it,
  // drags on the backdrop scroll-chained to the page and armed the Home
  // pull-to-refresh BEHIND the menu (audit H1). PullToRefresh reads this
  // lock and stands down.
  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  if (!mounted) return null;

  return (
    <div
      className="fixed inset-0 z-50"
      role="dialog"
      aria-modal="true"
      aria-label="More tools"
    >
      {/* Backdrop */}
      <button
        type="button"
        aria-label="Close menu"
        onClick={onClose}
        className={`absolute inset-0 bg-slate-950/60 transition-opacity duration-200 ${
          shown ? 'opacity-100' : 'opacity-0'
        }`}
      />
      {/* Panel */}
      <div
        onTransitionEnd={(e) => {
          if (e.target === e.currentTarget && !open) setMounted(false);
        }}
        className={`absolute z-10 inset-y-0 right-0 flex w-[300px] max-w-[85vw] flex-col overflow-y-auto bg-surface pb-8 shadow-2xl transition-transform duration-[250ms] ease-out ${
          shown ? 'translate-x-0' : 'translate-x-full'
        }`}
        style={{
          paddingTop: 'var(--de-top-inset, max(env(safe-area-inset-top), 1rem))',
        }}
      >
        <div className="flex items-center justify-between px-5 pb-2 pt-1">
          <h2 className="font-display text-lg font-bold text-ink">
            More tools
          </h2>
          <button
            type="button"
            aria-label="Close menu"
            onClick={onClose}
            className="flex h-8 w-8 items-center justify-center rounded-pill text-ink-muted active:bg-surface-subtle"
          >
            <Icon.Close className="h-4 w-4" />
          </button>
        </div>

        <nav aria-label="Tools" className="mt-1">
          <ul className="divide-y divide-line/30">
            {MORE_LINKS.map(({ to, label, blurb }) => (
              <li key={to}>
                <Link
                  to={to}
                  onClick={onClose}
                  className="flex items-center justify-between gap-3 px-5 py-3 active:bg-surface-subtle"
                >
                  <span className="min-w-0">
                    <span className="block text-sm font-semibold text-ink">
                      {label}
                    </span>
                    <span className="block truncate text-[11px] text-ink-muted">
                      {blurb}
                    </span>
                  </span>
                  <Icon.Chevron className="h-3.5 w-3.5 flex-none text-ink-subtle" />
                </Link>
              </li>
            ))}
          </ul>
        </nav>

        <div className="mt-auto border-t border-line/30">
          <Link
            to="/settings"
            onClick={onClose}
            className="flex items-center gap-3 px-5 py-3.5 active:bg-surface-subtle"
          >
            <Icon.Gear className="h-5 w-5 text-ink-muted" />
            <span className="text-sm font-semibold text-ink">Settings</span>
          </Link>
        </div>
      </div>
    </div>
  );
}
